/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PrimaryButton from "../../../components/_utils/CommonButtons/PrimaryButton";
import FilterORSearch from "../../../components/_utils/CommonSearchComponents/FilterORSearch";
import CommonToast from "../../../components/_utils/CommonToast";
import TopNavComponent from "../../../components/_utils/CommonTopNav/TopNavComponent";
import CardContainer from "../../../components/_utils/CommonViewAllCard/CardContainer";
import TextWithIcon from "../../../components/_utils/CommonViewAllCard/CardSubComponent/TextWithIcon";
import IfscCodeMapper from "../../../components/_utils/IfscLogoMapper";
import PaginationComponent from "../../../components/_utils/PaginationComponent";
import TickerAnimation from "../../../components/_utils/TickerAnimation";
import Loader from "../../../components/Loader/Index";

import {
  beneVerifyThisBankNames,
  beneVerifyThisIFSC,
} from "../../../redux/features/beneficiary/beneAllVerification/beneAllVerification";

import {
  GlobalConfiguration,
  IntervalConfiguration,
  PaginationConfiguration,
} from "../../../utils/configurationProvider";

import useCustomSearchParams from "../../../utils/useCustomSearchParams";
import Validation from "../../../utils/validation";
import "./index.css";
import ICONS from "../../../assets/Icons";

const IFSCVerification = () => {
  const dropdownOptions = PaginationConfiguration?.paginationOptions;

  const { searchParam, handleSearchParams } = useCustomSearchParams({
    pageno: GlobalConfiguration.lengthCheckOne,
    pagesize: dropdownOptions[GlobalConfiguration.lengthCheckZero],
    ifsc: "",
  });

  const inputRef = useRef("");
  const scrollRef = useRef(null);
  const reloadedRn = useRef(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [bankNames, setBankNames] = useState([]);
  const [selectedBank, setSelectedBank] = useState(
    !Validation.ifscRegex.test(searchParam.get("ifsc"))
      ? searchParam.get("ifsc")
      : "",
  );
  const [selectIFSC, setSelectIFSC] = useState(
    Validation.ifscRegex.test(searchParam.get("ifsc"))
      ? searchParam.get("ifsc")
      : "",
  );
  const [ifscDetails, setIfscDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    searchParam.get("pageno") || GlobalConfiguration?.lengthCheckOne,
  );
  const [ifscLoading, setIfscLoading] = useState("");
  const [noDataFound, setNoDataFound] = useState(false);
  const [prvBank] = useState("");
  const [prvIfsc, setPrvIfsc] = useState("");
  const [pageSize, setPageSize] = useState(dropdownOptions[0]);
  const [selectedValue, setSelectedValue] = useState(dropdownOptions[0]);
  const [ifscData, setIfscData] = useState([]);
  const [showPagination, setShowPagination] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [lastSearchedIFSC, setLastSearchedIFSC] = useState("");
  const [lastSearchedBank, setLastSearchedBank] = useState("");

  const { beneVerifyThisBankNamesLoading, beneVerifyThisBankNamesData } =
    useSelector((state) => state.beneAllVerification);

  const services = useSelector((state) => state.refreshToken.services);

  const handleIfscSearch = async () => {
    setNoDataFound(false);

    const isValidIFSC =
      selectIFSC?.length === GlobalConfiguration?.ifscLength &&
      Validation.ifscRegex.test(selectIFSC);

    if (selectedBank === "" && !selectIFSC) return;

    setLastSearchedIFSC(selectIFSC);
    setLastSearchedBank(selectedBank);

    if (selectIFSC && !isValidIFSC) {
      setShowPagination(false);
      CommonToast({
        text: "Invalid format for IFSC.",
        icon: ICONS?.INFO_FILLED,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });
      setNoDataFound(true);
      return;
    }

    setIfscLoading(true);
    let queryParams = {
      page_no: currentPage,
      page_size: pageSize,
      search_string:
        selectedBank !== ""
          ? selectedBank
          : selectIFSC.length === GlobalConfiguration?.ifscLength
            ? selectIFSC
            : "",
    };

    let response = await dispatch(beneVerifyThisIFSC(queryParams));
    if (
      response?.payload?.ifsc_details &&
      response?.payload?.title &&
      response?.payload?.total_count
    ) {
      handleSearchParams("ifsc", selectIFSC || selectedBank);
      setIfscData(response?.payload);

      let newData = response?.payload?.ifsc_details;

      setIfscDetails((prevData) =>
        currentPage === GlobalConfiguration?.lengthCheckOne ||
        prvBank !== selectedBank ||
        prvIfsc !== selectIFSC
          ? newData
          : [...(prevData || []), ...newData],
      );

      setIfscLoading(false);
      setPrvIfsc(selectIFSC);
      setShowPagination(true);
    } else if (response?.error) {
      setNoDataFound(true);
      CommonToast({
        text: response?.error?.message || "Invalid format for IFSC.",
        icon: ICONS?.INFO_FILLED,
        variant: "var(--error)",
        btnProps: {
          isDisabled: false,
          enabledTextColor: "var(--text-primary)",
          enabledBgColor: "var(--primary-bg)",
        },
      });

      setShowPagination(false);
      setIfscDetails([]);
      setIfscLoading(false);
    }
  };

  const handleIFSCDetails = (IFSC) => {
    if (Validation.ifscRegex.test(IFSC)) {
      let id = IFSC;
      navigate(`/settings/IFSC_verification/IFSC_Details/${id}`, {
        state: {
          isViewAll: true,
        },
      });
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (value) => {
    setSelectedValue(value);
    setPageSize(value);
    handleSearchParams("pagesize", value);
    handleSearchParams("pageno", GlobalConfiguration?.lengthCheckOne);
    setCurrentPage(GlobalConfiguration?.lengthCheckOne);
    toggleDropdown();
  };

  const handlePagination = (page) => {
    handleSearchParams("pageno", page);

    setCurrentPage(page);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData.getData("text") || "")
      .toUpperCase()
      .slice(
        GlobalConfiguration?.lengthCheckZero,
        GlobalConfiguration?.ifscLength,
      );

    setSelectIFSC(pastedText);
  };

  const canDoIFSCSearch = () => {
    if (reloadedRn.current) return false;

    const hasIFSC = selectIFSC?.length === GlobalConfiguration?.ifscLength;
    const hasBank = !!selectedBank;

    const isSameIFSC = selectIFSC === lastSearchedIFSC;
    const isSameBank = selectedBank === lastSearchedBank;

    if (!hasIFSC && !hasBank) return false;

    if (isSameIFSC && isSameBank) return false;

    return true;
  };

  const isIFSCButtonDisabled = !canDoIFSCSearch();

  const handleEnterKeyPress = (event) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    event.stopPropagation();

    if (isIFSCButtonDisabled) return;

    handleIfscSearch();
  };

  useEffect(() => {
    dispatch(beneVerifyThisBankNames());
    if (services && !services?.includes("VIFSC")) {
      navigate("/feature_not_assigned");
    }
  }, []);

  useEffect(() => {
    setBankNames(
      !beneVerifyThisBankNamesLoading
        ? beneVerifyThisBankNamesData?.bank_names
        : [],
    );
  }, [beneVerifyThisBankNamesLoading, beneVerifyThisBankNamesData]);

  useEffect(() => {
    const getNextData = async () => {
      if (selectedBank !== "" || Validation.ifscRegex.test(selectIFSC)) {
        let queryParams = {
          page_no: currentPage,
          page_size: pageSize,
          search_string:
            selectedBank !== ""
              ? selectedBank
              : selectIFSC.length === GlobalConfiguration?.ifscLength
                ? selectIFSC
                : "",
        };

        setIfscLoading(true);

        let response = await dispatch(beneVerifyThisIFSC(queryParams));

        if (
          response?.payload?.ifsc_details &&
          response?.payload?.title &&
          response?.payload?.total_count
        ) {
          setIfscData(response?.payload);
          let newData = response?.payload?.ifsc_details;
          setIfscDetails(newData);
          setShowPagination(true);
          setIfscLoading(false);
        } else if (response?.error) {
          CommonToast({
            text: response?.error?.message,
            icon: ICONS?.INFO_FILLED,
            variant: "var(--error)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          setTimeout(() => {
            setIfscDetails([]);
            setIfscLoading(false);
            setShowPagination(false);
          }, IntervalConfiguration?.intervalMedium);
        }
      }
    };
    getNextData();
  }, [pageSize, currentPage]);

  useEffect(() => {
    document.addEventListener("keydown", handleEnterKeyPress);
    return () => {
      document.removeEventListener("keydown", handleEnterKeyPress);
    };
  }, [isIFSCButtonDisabled, selectIFSC, selectedBank]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = GlobalConfiguration?.lengthCheckZero;
    }
  }, [currentPage]);

  return (
    <div className="h-full-height relative overflow-hidden flex flex-col overflow-y-hidden">
      <div className="hidden Qmd:flex Qsmd:min-h-navbar-breadcrumb-height bg-primary-nav"></div>
      {/* Pagination component */}
      {showPagination && (
        <div
          style={{
            position: "absolute",
            zIndex: "var(--z-index-header)",
          }}
          className="h-pagination-height bottom-bottom-base w-width-full"
        >
          <PaginationComponent
            handleSelect={handleSelect}
            selectedValue={selectedValue}
            data={ifscData}
            pageSize={pageSize}
            dropdownOptions={dropdownOptions}
            handlePagination={handlePagination}
            currentPage={currentPage}
            setIsOpen={setIsOpen}
          />
        </div>
      )}

      <div className="bg-primary-bg flex flex-1 flex-col overflow-y-hidden">
        <div className="Qsmd:hidden bg-primary-nav shadow-box-shadow-bottom fixed top-top-base left-left-base z-z-index-tooltip w-width-full block">
          <TopNavComponent
            isFormattingNotRequired={true}
            topNavHeader="IFSC Verification"
          />
        </div>

        {/* Search field */}
        <div className="bg-primary-nav">
          <div className="Qlg:w-max-content-width mx-auto">
            <div className="w-width-full bg-primary-nav p-mobile-padding-l Qmd:p-padding-md Qlg:px-padding-base mt-margin-xl Qmd:mt-margin-base">
              <FilterORSearch
                dropdownPlaceholder="Select bank to fetch IFSC"
                dropdownOptions={bankNames}
                dropdownSelected={selectedBank}
                onPaste={handlePaste}
                dropdownSetSelected={(val) => {
                  reloadedRn.current = false;
                  setSelectedBank(val);
                  setSelectIFSC("");
                  setIfscLoading(false);
                }}
                dropdownSearchPlaceholder="Search bank"
                dropdownDisabled={selectIFSC !== "" ? true : false}
                searchText={selectIFSC}
                searchRef={inputRef}
                searchPlaceholder="Search 11 digit IFSC code"
                isSearchDisabled={selectedBank !== ""}
                onSearchInput={(val) => {
                  reloadedRn.current = false;
                  const upperVal = val
                    .toUpperCase()
                    .slice(
                      GlobalConfiguration?.lengthCheckZero,
                      GlobalConfiguration?.ifscLength,
                    );
                  setSelectIFSC(upperVal);
                  if (true) {
                    setSelectedBank("");
                  }
                  setIfscLoading(false);
                }}
                onClick={() => {
                  if (canDoIFSCSearch()) {
                    handleIfscSearch();
                  }
                }}
                isBtnDisable={!canDoIFSCSearch()}
                txtColor={
                  !canDoIFSCSearch()
                    ? "var(--text-primary)"
                    : "var(--primary-bg)"
                }
                btnColor={
                  !canDoIFSCSearch()
                    ? "var(--text-disabled)"
                    : "var(--text-primary)"
                }
                btnText="Fetch"
                inActiveVariantNeeded
              />
            </div>
          </div>
        </div>
        {ifscLoading ? (
          <>
            <div className="flex justify-center items-center h-screen overflow-hidden">
              <Loader />
            </div>
          </>
        ) : noDataFound ? (
          <>
            <div className="flex flex-col justify-center items-center m-auto text-center h-full-height">
              <img
                src={ICONS?.NO_DATA_FOUND}
                alt="No data found"
                className="mb-margin-md w-graphic-width-md h-graphic-rectangle-height-md"
              />
              <p className="text-font-sm text-text-primary text-center font-normal font-sans mb-margin-sm">
                No data found.
              </p>
              <PrimaryButton
                enabledBgColor="var(--primary-nav-act)"
                btnText="Clear parameters"
                enabledTextColor="var(--text-primary)"
                handleFunction={() => {
                  setSelectIFSC("");
                  setSelectedBank("");

                  setLastSearchedIFSC("");
                  setLastSearchedBank("");

                  setIfscDetails([]);
                  setNoDataFound(false);
                  setIfscDetails([]);

                  setIfscLoading(false);
                  setShowPagination(false);
                }}
                buttonRef={null}
                hoverOption="white"
              />
            </div>
          </>
        ) : ifscDetails.length > GlobalConfiguration?.lengthCheckZero ? (
          <>
            <div
              className="overflow-y-scroll flex flex-col items-center h-full-height scroll-py-top_md mb-[var(--pagination-height)]"
              ref={scrollRef}
            >
              {/* <div className="overflow-y-scroll flex-1 relative z-z-index-content"> */}
              <div className="standard-card-alignment gap-y-gap-md gap-x-gap-sm overflow-y-visible px-mobile-padding-l Qmd:px-padding-md">
                {ifscDetails.map((ifscDtls) => {
                  return (
                    <CardContainer
                      cardBgColor="primary-card"
                      onClickHandler={() => {
                        handleIFSCDetails(ifscDtls?.ifsc);
                      }}
                    >
                      <card-body>
                        <TextWithIcon
                          text={ifscDtls?.ifsc}
                          textColor="var(--text-primary)"
                          subText="IFSC code"
                        />
                        <div className="flex gap-x-gap-sm justify-between items-center">
                          <div className="flex flex-1 truncate flex-col gap-y-gap-xs">
                            <p className="text-font-sm text-text-primary flex-1 truncate font-normal pl-padding-sm">
                              <TickerAnimation
                                text={ifscDtls?.branch?.toLowerCase()}
                              />
                            </p>

                            <div className="w-full relative flex pl-padding-sm justify-between items-center">
                              <p className="text-font-xs text-text-secondary flex-1 truncate font-normal">
                                Branch location
                              </p>
                            </div>
                          </div>

                          <div className="bg-text-primary flex items-center gap-x-gap-sm rounded-l-border-radius-md py-padding-xs px-padding-sm">
                            <div className="Qsmd:w-banklogo-width-sm Qsmd:h-banklogo-height-sm w-banklogo-width-mobile h-banklogo-height-mobile flex-shrink-0">
                              <IfscCodeMapper ifscCode={ifscDtls.ifsc} />
                            </div>
                          </div>
                        </div>
                      </card-body>
                    </CardContainer>
                  );
                })}
              </div>
              {/* </div> */}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col justify-center items-center m-auto text-center h-full-height">
              <img
                src={ICONS?.IFSC_VERIFICATION_GRAPHICS}
                alt="Enter Bank or Enter 11 digit IFSC"
                className="mb-margin-md
                  h-graphic-square-height-md w-graphic-width-md"
              />
              <p className="text-font-sm text-text-primary text-center font-normal font-sans">
                Select bank or enter 11-digit IFSC code in the search bar, to{" "}
                <br />
                fetch the IFSC details.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IFSCVerification;
